import React, { useState, useEffect, useRef, useCallback } from 'react'
import styled from 'styled-components'
import { batch, connect } from 'react-redux'
import { Form, Input, Button, Tabs, Modal, Checkbox, notification, message } from 'antd'
import * as _ from 'lodash'
import { Icon } from 'dlweb-components'
import { v4 as uuidv4 } from 'uuid'
import axios from 'axios'
import ProfileProperties from '../../ProfileProperties'
import WorkflowForm from '../../WorkflowForm'
import NotificationForm from '../../NotificationForm'
import { fileService } from '../../../services/fileService'
import profileService from '../../../services/profileService'
import FileTable from './FileTable'
import useFileUpload from '../../../hooks/useFileUpload'

const isWebView2 = () => typeof window !== 'undefined' && !!(window.chrome && window.chrome.webview)

const CheckinDialog = ({ maxFileSize, workingFolder = "Documents", angularScope, checkinFiles = [], hideModal }) => {

  const [form] = Form.useForm()
  const inputRef = useRef(null)

  const [files, setFiles] = useState([])
  const [webView2UploadFiles, setWebView2UploadFiles] = useState([])
  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(false)
  const [disabled, setDisabled] = useState(!isWebView2())
  const [processing, setProcessing] = useState(false)
  const [userInputValue, setUserInputValue] = useState('')
  const [folderSettings, setFolderSettings] = useState({})
  const [currentPropertyValues, setCurrentPropertyValues] = useState({})
  const [selectedFile, setSelectedFile] = useState(null)

  //useEffect(() => { setFiles(checkinFiles) }, [checkinFiles])

  useEffect(async () => {
    if (!_.isEmpty(checkinFiles)) {
      //const checkedOutFiles = checkinFiles.filter(file => file.CheckedOutBy == angularScope.userName || file.checkedOutBy == angularScope.userName); // change to use Redux state user
      // const documentInfo = await fileService.getDocumentsInfo(checkinFiles.map(file => ({ id: file.ID || file.fileId, versionId: file.VersionID || 0, minor: file.Minor || 0 })));
      const documentInfo = await fileService.getDocumentsInfo(checkinFiles.map(({ ID, fileId, VersionID, Minor }) => ({
        id: ID ?? fileId,
        versionId: VersionID ?? 0,
        minor: Minor ?? 0,
      })));
      setFiles(documentInfo.data);
      setSelectedFile(documentInfo.data[0]);
    }
  }, [checkinFiles]);

  // non-webview selected source file handles
  const [sourceFiles, setSourceFiles] = useState({}) // { [fileId]: File }

  const handleUploadsComplete = async (batch, batchFiles = null) => {
    const controller = new AbortController()
    const signal = controller.signal
    window.currentAbortController = controller
    window.currentAbortController.batchId = batch.batchId

    let uploadError = false;

    for (const file of files) {
      let fileName = ''
      const webview = isWebView2()
      if (webview) {
        fileName = `${file.Name}${file.Type ? `.${file.Type}` : ''}`
      } else {
        fileName = sourceFiles[file.ID]?.name
      }

      const postData = {
        batchId: batch.batchId,
        fileId: file.ID,
        isWebView: webview,
        sourceFileName: fileName,
        version: file.Version,
        minor: file.Minor,
        versionNotes: batch.versionNotes || '',
        versionAll: batch.versionAll || false,
        keepCheckedOut: batch.keepCheckedOut || false,
        maintainVersionLevel: batch.maintainVersionLevel || false,
        properties: currentPropertyValues[file.ID]?.properties.map((p) => ({ name: p.name, value: p.value })) || [],
        isProject: false,
        ...batch.notificationForm,
        ...batch.workflowForm
      }

      const processError = (err) => {
        if (err) {
          uploadError = true;
          console.error('Error checking in file:', err)
          setProcessing(false)
          setLoading(false)
          notification.error({ message: `File ${file.Name}${file.Type ? `.${file.Type}` : ''} could not be checked in` })
        }
      }

      const checkinFile = async () => {
        const { data: res } = await axios.post('/api/documents/checkin', postData, { signal })
        if (res) {
          processError(res)
        } else {
          // no-op; server returns empty body on success
        }
      }

      await checkinFile()
    }

    if (!uploadError) {
      const fileIds = files.map((f) => f.ID).join('::')

      if (batch.notificationForm.messageOption === '1') {
        const postData = { files: fileIds, ...batch.notificationForm }
        await axios.post('/api/documents/createNotification', postData)
      }

      if (batch.workflowForm.routeOption === '1' && batch.workflowForm.routeId !== '0') {
        const postData = { files: fileIds, ...batch.workflowForm }
        await axios.post('/api/documents/startWorkflow', postData)
      }

      if (isWebView2()) {
        try { window.chrome.webview.postMessage({ action: 'deletelocalfiles', payload: webView2UploadFiles }) } catch {}
      }
      hideModal()
      angularScope.$broadcast('reloadGrid')
      notification.success({ message: `File${files?.length > 1 ? 's' : ''} checked in successfully` })
    }

    uploadError = false;
  }

  const { uploadFiles, cancelUpload, pauseUpload, resumeUpload, uploadProgress, isUploading, cancelCleanup } = useFileUpload(handleUploadsComplete)

  const cancelCheckin = () => {
    if (window.currentAbortController) {
      window.currentAbortController.abort()
      cancelCleanup(window.currentAbortController.batchId)
      window.currentAbortController = null
    }
  }

  useEffect(() => {
    if (files.length === 0) {
      return;
    }

    // if in webview, verify files exist locally through host
    if (isWebView2()) {
      fileService.getWorkingPaths(files.map((file) => file.ID)).then((res) => {
        const pathCheckCallback = (fileList) => {
          setWebView2UploadFiles(fileList)
          const badList = _.filter(fileList, (o) => !o.existsLocally)
          if (badList.length > 0) {
            message.warning({
              content: (
                <>
                  <div>The following files were not found and will not be included in the check in:</div>
                  {badList.map((badFile, index) => (
                    <div key={index} style={{ textAlign: 'left' }}>
                      {badFile.fileName}
                    </div>
                  ))}
                </>
              ),
              duration: 5
            })
            if (fileList.length === badList.length) {
              hideModal()
            }
          }
        }

        window.chrome.webview.addEventListener('message', (event) => pathCheckCallback(JSON.parse(event.data)), { once: true })
        window.chrome.webview.postMessage({ action: 'checkPaths', payload: res.data })
      })
    }

    const setFormValues = async () => {
      const fs = await axios.get(`/api/wt/documents/import/settings/read?folderId=${workingFolder}`)
      setFolderSettings(fs.data.data)
    }
    setFormValues()
  }, [workingFolder, files])

  // NEW: build a files array suitable for the upload hook in both modes
  const buildUploadFiles = useCallback(() => {
    if (isWebView2()) {
      // synthesize lightweight file descriptors; UploadManager will return fileUrl+size by relativePath/name
      return webView2UploadFiles.map((f) => ({
        objectId: f.objectId,
        name: f.fileName,
        localPath: f.fullLocalFilePath,
        relativePath: f.fileName
      }))
    }
    // browser: actual File objects chosen by the user
    return Object.values(sourceFiles)
  }, [webView2UploadFiles, sourceFiles])

  const handleSubmit = async (forms) => {
    setLoading(true)

    // validate required properties before calling the server
    for (const [fileId, profileData] of Object.entries(currentPropertyValues)) {
      const isValid = await profileService.validateRequiredProperties(profileData.properties)
      if (!isValid) {
        setLoading(false)
        return
      }
    }

    const { notificationForm, workflowForm } = forms

    const uploadBatch = {
      batchId: uuidv4(),
      files: buildUploadFiles(),
      meta: {
        versionNotes: form.getFieldValue('versionNotes') || '',
        versionAll: form.getFieldValue('versionAll') || false,
        keepCheckedOut: form.getFieldValue('keepCheckedOut') || false,
        maintainVersionLevel: form.getFieldValue('maintainVersionLevel') || false,
        notificationForm: notificationForm.getFieldsValue(),
        workflowForm: workflowForm.getFieldsValue()
      }
    }

    // unified path: the hook handles both browser and WebView2 internally
    setProcessing(true)
    setStatus('Checking in files...')
    uploadFiles(uploadBatch)
  }

  useEffect(() => {
    const unmatched = files.filter((file) => !(file.ID in sourceFiles))
    if (unmatched.length === 0 && !isWebView2()) {
      setDisabled(false)
    }
  }, [sourceFiles])

  const showLocalFilePicker = async (record) => {
    try {
      const pickerOptions = { multiple: false, excludeAcceptAllOption: false }
      const fileHandle = await fileService.openFileBrowser(pickerOptions)
      const file = await fileHandle.getFile()

      const alreadyExists = Object.values(sourceFiles).some((f) => f.name === file.name)
      if (alreadyExists) {
        message.error(`This file has already been added as a source: ${file.name}`)
        return
      }

      setSourceFiles((prev) => ({ ...prev, [record.ID]: file }))
    } catch (err) {
      // user cancelled picker
    }
  }

  const onSourceSelect = (file) => {}

  const onClose = () => {
    if (processing) {
      pauseUpload()
      Modal.confirm({
        okText: 'Yes',
        cancelText: 'No',
        title: 'Import in progress',
        content: 'Are you sure you want to cancel the import?',
        onCancel: () => {
          resumeUpload()
        },
        onOk: () => {
          if (isUploading) {
            cancelUpload()
          } else {
            cancelCheckin()
          }
          hideModal()
        }
      })
    } else {
      hideModal()
    }
  }

  const onRemoveFile = (file) => {
    setFiles((prev) => {
      const updated = prev.filter((f) => f.ID !== file.ID)
      if (selectedFile && selectedFile.ID === file.ID) {
        setSelectedFile(updated[0])
      }
      return updated
    })
  }

  return (
    <Form.Provider onFormFinish={(name, { forms }) => handleSubmit(forms)}>
      <Container id="import-container" className="wide-modal-container">
        {processing && <ProcessingOverlay />}
        {processing && (
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
            <Icon type="circle-notch" size="lg" spin style={{ fontSize: '40px', margin: 'auto', display: 'block', color: 'var(--primary)' }} />
            <div style={{ margin: 'auto', marginTop: '5px', fontSize: '22px' }}>{status}</div>
          </div>
        )}
        <Tabs
          type="card"
          animated={false}
          defaultActiveKey="1"
          destroyInactiveTabPane={false}
          tabBarStyle={{ width: '100%' }}
          style={{ height: '100%' }}
          items={[
            {
              label: (
                <div>
                  <Icon type="list" size="lg" style={{ marginRight: '10px' }} />
                  Files
                </div>
              ),
              key: '1',
              children: (
                <div style={{ display: 'flex', height: '100%' }}>
                  <LeftContainer>
                    <Form name="mainForm" form={form} layout="vertical" labelAlign="left" initialValues={{ versionNotes: '' }}>
                      <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Form.Item style={{ flex: '40% 1', width: '100%', overflow: 'hidden', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '6px' }}>
                          <FileTable
                            selectedFile={selectedFile}
                            setSelectedFile={setSelectedFile}
                            onSourceSelect={onSourceSelect}
                            files={files}
                            sourceFiles={sourceFiles}
                            processing={processing}
                            isUploading={isUploading}
                            fileProgress={uploadProgress}
                            showLocalFilePicker={showLocalFilePicker}
                            removeFile={onRemoveFile}
                          />
                        </Form.Item>
                        <Form.Item label="Version notes" name="versionNotes" style={{ flex: 1, padding: '0', margin: 0, marginTop: '5px' }} className="flex-item">
                          <Input.TextArea ref={inputRef} maxLength={1000} value={userInputValue} onChange={(e) => { setUserInputValue(e.target.value) }} style={{ resize: 'none', height: '100%' }} />
                        </Form.Item>
                        <Form.Item colon={false} name="versionAll" valuePropName="checked">
                          <Checkbox defaultChecked>Apply version notes to all documents</Checkbox>
                        </Form.Item>
                        <Form.Item colon={false} name="keepCheckedOut" valuePropName="checked">
                          <Checkbox>Keep checked out</Checkbox>
                        </Form.Item>
                        <Form.Item colon={false} name="maintainVersionLevel" valuePropName="checked">
                          <Checkbox>Maintain existing version level</Checkbox>
                        </Form.Item>
                      </div>
                    </Form>
                  </LeftContainer>
                  <div style={{ height: '100%', width: '50%', marginLeft: '10px', overflowY: 'hidden' }}>
                    <ProfileProperties showProfile={false} selectedItems={[selectedFile]} setCurrentValues={setCurrentPropertyValues} />
                  </div>
                </div>
              )
            },
            {
              label: (
                <div>
                  <Icon type="envelope" size="lg" style={{ marginRight: '10px' }} />
                  Notifications
                </div>
              ),
              key: '2',
              forceRender: true,
              children: (
                <div style={{ padding: '15px', height: '100%' }}>
                  <NotificationForm />
                </div>
              )
            },
            {
              label: (
                <div>
                  <Icon type="cogs" size="lg" style={{ marginRight: '10px' }} />
                  Workflow
                </div>
              ),
              key: '3',
              forceRender: true,
              children: (
                <div style={{ padding: '15px', height: '100%' }}>
                  <WorkflowForm workingFolder={workingFolder} folderSettings={folderSettings} />
                </div>
              )
            }
          ]}
        />
      </Container>
      <ButtonContainer>
        <Button type="primary" style={{ float: 'right', marginLeft: '10px' }} loading={loading} disabled={disabled} form="mainForm" htmlType="submit">
          Check In File{files?.length > 1 && 's'}
        </Button>
        <Button style={{ float: 'right' }} onClick={onClose}>
          Cancel
        </Button>
      </ButtonContainer>
    </Form.Provider>
  )
}

const Container = styled.div`
  .ant-tabs-nav { margin-bottom: 0; }
  .ant-tabs-tab:first-child { border-left: 1px solid #f0f0f0 !important; }
  .ant-form-item { margin: 0px 0; }
  .ant-input-group-addon { padding: 0; }
  .ant-table-thead > tr > th { background-color: #fff; border-top: 1px solid #f0f0f0; }
  .ant-form { height: 100%; }
  .ant-input-group-addon { background-color: #fff; }
  .flex-item .ant-row { display: block; height: 100%; }
  .flex-item .ant-row div:nth-child(2) { height: calc(100% - 35px); }
  .ant-list-empty-text { display: none; }
  .flex-item-list .ant-row div:nth-child(2) { height: calc(100% - 25px); }
  .flex-item > .ant-row > div:nth-child(2) div:not(.file-icon-small) { height: 100%; }
  .ant-form-item-control-input { min-height: 0; }
`

const LeftContainer = styled.div`
  width: 50%; height: 100%; background: #fafafa; display: inline-block; padding: 10px; vertical-align: top;
`

const ProcessingOverlay = styled.div`
  height: 100%; width: 100%; background-color: #fff; opacity: .6; position: absolute; top: 0; left: 0; z-index: 9;
`

const ButtonContainer = styled.div`
  position: absolute; bottom: 20px; right: 25px;
`

const mapStateToProps = (state) => {
  const { maxFileSize } = state.app
  const { id: workingFolder } = state.workingFolder
  return { maxFileSize, workingFolder }
}

export default connect(mapStateToProps, null)(CheckinDialog)
